import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { FormControl, TextField, Autocomplete, Typography } from '@mui/material'

// Issue form components
import { FormTable, FormTableRow } from './FormTable'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { RegisteredIsinField } from '../../../components/fields/IsinField'
import { RegisteredIntegerField } from '../../../components/fields/NumericField'
import MarketplacesApi from '../../../api/MarketplacesApi'

// Constants
import { countryCodes, currencies, countryCodesKeyValuePairs, currenciesKeyValuePairs } from './constants'
import { ShareEntity } from 'types/ShareEntity'
import { FormIssueEntity } from '../types/FormIssueEntity'
import { Marketplace } from 'types/Marketplace'

const marketplaceApi = new MarketplacesApi()

function IpoFields({
    newIpo = false,
    share,
    issue,
}: { newIpo?: boolean; share?: ShareEntity; issue?: FormIssueEntity } & (
    | { newIpo: true; share: ShareEntity }
    | { newIpo: false; issue: FormIssueEntity }
)) {
    const [marketplaces, setMarketplaces] = useState<Array<Marketplace>>([])
    const formMethods = useFormContext()
    const newInstrument = formMethods.watch('newInstrument')

    useEffect(() => {
        const fetchData = async () => {
            const fetchedMarketplaces = await marketplaceApi.allMarketplaces()

            const filteredAndSortedMarketplaces = fetchedMarketplaces
                .filter((marketplace) => marketplace.listName)
                .sort((a, b) => (a.listName > b.listName ? 1 : -1))
            setMarketplaces(filteredAndSortedMarketplaces)
        }
        fetchData()
    }, [])

    useEffect(() => {
        formMethods.setValue('newInstrument', false)
    }, [share])

    const canAddNewInstrumentToExistingCompany = newIpo && share

    const otherCompanyInstrumentIds = issue
        ? issue.ipoData.companyInstrumentIds.filter((id) => id !== issue.instrumentId)
        : []

    const otherCompanyShareIds = issue ? issue.ipoData.companyShareIds.filter((id) => id !== issue.shareId) : []

    const disableUpdatingCompanyName = [...otherCompanyInstrumentIds, ...otherCompanyShareIds].length > 0

    return (
        <FormTable>
            {canAddNewInstrumentToExistingCompany ? (
                <FormTableRow label='Ny aktie'>
                    <RegisteredCheckBox label='Ny aktie till existerande bolag' name='newInstrument' />
                </FormTableRow>
            ) : null}
            {!newIpo ? (
                <FormTableRow label='Bolagsnamn'>
                    {!disableUpdatingCompanyName ? (
                        <RegisteredTextField label='Bolagsnamn' name='ipoData.companyName' />
                    ) : (
                        <Typography>{issue.ipoData.companyName}</Typography>
                    )}
                </FormTableRow>
            ) : null}
            {newIpo && (!share || newInstrument) ? (
                <FormTableRow label='Aktienamn'>
                    <RegisteredTextField label='Aktienamn' name='ipoData.instrumentName' />
                </FormTableRow>
            ) : null}

            {!newIpo && (!share || newInstrument) ? (
                <>
                    <FormTableRow label='Aktiens Symbol'>
                        <RegisteredTextField label='Symbol' name='ipoData.instrumentSymbol' />
                    </FormTableRow>
                    {issue ? (
                        <>
                            <FormTableRow label='Aktiens ISIN-kod'>
                                <RegisteredIsinField label='ISIN-kod' name='ipoData.instrumentIsin' />
                            </FormTableRow>
                            <FormTableRow label='Antal aktier i bolaget'>
                                <RegisteredIntegerField label='Antal aktier' name='ipoData.companyTotalShares' />
                            </FormTableRow>
                        </>
                    ) : null}
                    {!newInstrument ? (
                        <FormTableRow label='Land'>
                            <FormControl sx={{ width: '176px' }}>
                                <Controller
                                    name='ipoData.countryCode'
                                    defaultValue={null}
                                    render={({ field: { onChange, onBlur, value } }) => {
                                        if (typeof value === 'string') {
                                            value =
                                                countryCodesKeyValuePairs[
                                                    value as keyof typeof countryCodesKeyValuePairs
                                                ]
                                        }
                                        return (
                                            <Autocomplete
                                                fullWidth
                                                options={countryCodes}
                                                getOptionLabel={(option) => option.label}
                                                value={value}
                                                onBlur={onBlur}
                                                onChange={(event, newValue) => {
                                                    onChange(newValue.id)
                                                }}
                                                renderInput={(params) => <TextField {...params} label='Land' />}
                                                isOptionEqualToValue={(option, v) => option.id === v.id}
                                            />
                                        )
                                    }}
                                />
                            </FormControl>
                        </FormTableRow>
                    ) : null}
                    <FormTableRow label='Valuta'>
                        <FormControl sx={{ width: '176px' }}>
                            <Controller
                                name='ipoData.currency'
                                render={({ field: { onChange, onBlur, value } }) => {
                                    if (typeof value === 'string') {
                                        value = currenciesKeyValuePairs[value as keyof typeof currenciesKeyValuePairs]
                                    }
                                    return (
                                        <Autocomplete
                                            fullWidth
                                            options={currencies}
                                            getOptionLabel={(option) => option.label}
                                            value={value}
                                            onBlur={onBlur}
                                            onChange={(event, newValue) => {
                                                onChange(newValue.id)
                                            }}
                                            renderInput={(params) => <TextField {...params} label='Valuta' />}
                                            isOptionEqualToValue={(option, v) => option.id === v.id}
                                        />
                                    )
                                }}
                            />
                        </FormControl>
                    </FormTableRow>
                    <FormTableRow label='Lista'>
                        {marketplaces.length > 0 ? (
                            <FormControl sx={{ width: '276px' }}>
                                <Controller
                                    name='ipoData.marketplaceId'
                                    render={({ field: { onChange, onBlur, value } }) => {
                                        if (typeof value === 'string') {
                                            value = marketplaces.find(({ id }) => id === value)
                                        }
                                        return (
                                            <Autocomplete
                                                fullWidth
                                                options={marketplaces}
                                                getOptionLabel={(option) => option.listName}
                                                value={value}
                                                onBlur={onBlur}
                                                onChange={(event, newValue) => {
                                                    onChange(newValue.id)
                                                }}
                                                renderInput={(params) => <TextField {...params} label='Lista' />}
                                                isOptionEqualToValue={(option, v) => option.id === v.id}
                                            />
                                        )
                                    }}
                                />
                            </FormControl>
                        ) : (
                            <Typography>Kunde inte hämta marknadsplatser</Typography>
                        )}
                    </FormTableRow>
                </>
            ) : null}
        </FormTable>
    )
}

export default IpoFields
